import { Link } from 'react-router-dom';

import { PageMeta } from '@/components/PageMeta';
import EmailLink from '@/components/ui/EmailLink.tsx';
import { ROUTES } from '@/config/routes';
import { useSaasStore } from '@/stores/saas-store.ts';
import { Building2, Mail, Scale } from 'lucide-react';

export default function MentionsLegales() {
  const playerPlusEnabled = useSaasStore(s => s.playerPlusEnabled);
  return (
    <>
      <PageMeta
        title='Mentions légales'
        description='Mentions légales de BoulesConnect (MADSAAS SAS). Éditeur, hébergeur, responsable de traitement et informations légales obligatoires.'
        robots='index, follow'
      />

      {/* Hero */}
      <section className='pt-40 pb-16 px-6'>
        <div className='max-w-4xl mx-auto text-center'>
          <div className='inline-flex items-center gap-2 bg-blue-50 border border-blue-100 px-4 py-2 rounded-full mb-6'>
            <Scale className='w-4 h-4 text-blue-600' />
            <span className='text-xs font-bold text-blue-700 uppercase tracking-wider'>Informations légales</span>
          </div>
          <h1 className='text-4xl md:text-5xl font-extrabold text-slate-900 mb-4'>Mentions légales</h1>
          <p className='text-lg text-slate-500'>
            Conformément aux obligations de la loi pour la confiance dans l'économie numérique (LCEN).
          </p>
        </div>
      </section>

      {/* Content */}
      <section className='px-6 pb-20'>
        <div className='max-w-4xl mx-auto space-y-8'>
          {/* Éditeur */}
          <div className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
            <div className='flex items-center gap-3 mb-6'>
              <div className='w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center'>
                <Building2 className='w-5 h-5 text-blue-600' />
              </div>
              <h2 className='text-xl font-extrabold text-slate-900'>Éditeur du site</h2>
            </div>
            <div className='text-sm text-slate-600 space-y-2 leading-relaxed'>
              <p>
                <strong className='text-slate-900'>Dénomination sociale :</strong> MADSAAS SAS
              </p>
              <p>
                <strong className='text-slate-900'>Forme juridique :</strong> Société par actions simplifiée (SAS)
              </p>
              <p>
                <strong className='text-slate-900'>Capital social :</strong> 1 000 €
              </p>
              <p>
                <strong className='text-slate-900'>SIREN :</strong> 821 350 600
              </p>
              <p>
                <strong className='text-slate-900'>SIRET :</strong> 821 350 600 00036
              </p>
              <p>
                <strong className='text-slate-900'>N° TVA intracommunautaire :</strong> FR00821350600
              </p>
              <p>
                <strong className='text-slate-900'>Code APE :</strong> 6202A
              </p>
              <p>
                <strong className='text-slate-900'>RCS :</strong> Gap
              </p>
              <p>
                <strong className='text-slate-900'>Siège social :</strong>
              </p>
              <div className='ml-4 text-slate-500'>
                <div>05110 Lardier-et-Valença, France</div>
              </div>
              <p className='mt-4'>
                <strong className='text-slate-900'>Directeur de la publication :</strong> Marc-Antoine DUTOIT, en
                qualité de Président
              </p>
            </div>
          </div>

          {/* Hébergeur */}
          <div className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
            <h2 className='text-xl font-extrabold text-slate-900 mb-4'>Hébergeur</h2>
            <div className='text-sm text-slate-600 leading-relaxed space-y-2'>
              <p>
                <strong className='text-slate-900'>OVH SAS</strong>
              </p>
              <p>2 rue Kellermann, 59100 Roubaix, France</p>
              <p>Téléphone : +33 9 72 10 10 07</p>
              <p>
                Site :{' '}
                <a
                  href='https://www.ovhcloud.com'
                  className='text-blue-600 hover:underline'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  ovhcloud.com
                </a>
              </p>
              <p className='mt-2 text-slate-500 text-xs'>
                Datacenters : Roubaix et Gravelines (France) — certifiés ISO 27001 et ISO 27701.
              </p>
            </div>
          </div>

          {/* Responsable de traitement */}
          <div className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
            <h2 className='text-xl font-extrabold text-slate-900 mb-4'>Responsable du traitement des données</h2>
            <div className='text-sm text-slate-600 leading-relaxed space-y-2'>
              <p>
                Le responsable du traitement des données personnelles collectées via la plateforme BoulesConnect est :
              </p>
              <p className='mt-2'>
                <strong className='text-slate-900'>MADSAAS SAS</strong>
              </p>
              <p>Représentée par Marc-Antoine DUTOIT, Président</p>
              <p>
                Email : <EmailLink user='dpo' domain='boulesconnect.fr' className='text-blue-600 hover:underline' />
              </p>
              <p className='mt-3'>
                Pour plus d'informations sur le traitement des données et l'exercice de vos droits, consultez notre{' '}
                <Link to={ROUTES.POLITIQUE_RGPD} className='text-blue-600 hover:underline'>
                  Politique de Confidentialité et RGPD
                </Link>
                .
              </p>
            </div>
          </div>

          {/* DPO / Référent protection des données */}
          <div className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
            <h2 className='text-xl font-extrabold text-slate-900 mb-4'>Référent protection des données</h2>
            <div className='text-sm text-slate-600 leading-relaxed'>
              <p>
                Pour toute question relative au traitement de vos données personnelles et à l'exercice de vos droits
                (accès, rectification, suppression, portabilité, opposition, limitation), contactez-nous à{' '}
                <EmailLink user='dpo' domain='boulesconnect.fr' className='text-blue-600 hover:underline' />.
              </p>
              <p className='mt-2'>
                En cas de réponse insatisfaisante, vous pouvez introduire une réclamation auprès de la{' '}
                <a
                  href='https://www.cnil.fr/fr/plaintes'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-blue-600 hover:underline'
                >
                  CNIL
                </a>
                .
              </p>
            </div>
          </div>

          {/* Propriété intellectuelle */}
          <div className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
            <h2 className='text-xl font-extrabold text-slate-900 mb-4'>Propriété intellectuelle</h2>
            <div className='text-sm text-slate-600 leading-relaxed space-y-3'>
              <p>
                L'ensemble des éléments composant la plateforme BoulesConnect (textes, graphismes, logos, icônes,
                images, code source, bases de données) est la propriété exclusive de MADSAAS SAS ou fait l'objet d'une
                autorisation d'utilisation. Toute reproduction, représentation, modification, publication, adaptation de
                tout ou partie des éléments du site, quel que soit le moyen ou le procédé utilisé, est interdite sans
                autorisation écrite préalable.
              </p>
              <p>
                Les marques, logos et noms de produits ou de services tiers apparaissant sur la plateforme sont la
                propriété de leurs détenteurs respectifs.
              </p>
            </div>
          </div>

          {/* Contact */}
          <div className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
            <div className='flex items-center gap-3 mb-6'>
              <div className='w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center'>
                <Mail className='w-5 h-5 text-blue-600' />
              </div>
              <h2 className='text-xl font-extrabold text-slate-900'>Contact</h2>
            </div>
            <div className='text-sm text-slate-600 leading-relaxed'>
              <p>Pour toute question relative à la plateforme BoulesConnect :</p>
              <div className='mt-3 p-4 bg-slate-50 rounded-2xl'>
                <p>
                  <strong className='text-slate-900'>Email :</strong>{' '}
                  <EmailLink user='dpo' domain='boulesconnect.fr' className='text-blue-600 hover:underline' />
                </p>
                <p className='mt-1'>
                  <strong className='text-slate-900'>Courrier :</strong> MADSAAS SAS — 05110 Lardier-et-Valença, France
                </p>
              </div>
            </div>
          </div>

          {/* Litiges */}
          <div className='bg-white rounded-3xl shadow-sm border border-slate-100 p-8'>
            <h2 className='text-xl font-extrabold text-slate-900 mb-4'>Droit applicable et juridiction</h2>
            <div className='text-sm text-slate-600 leading-relaxed space-y-3'>
              <p>
                Les présentes mentions légales sont régies par le droit français. En cas de litige et après l'échec de
                toute tentative de recherche d'une solution amiable, les tribunaux de Gap (05) seront seuls compétents,
                sans préjudice des règles de compétence protectrice du consommateur prévues à l'article R.631-3 du Code
                de la consommation.
              </p>
              {playerPlusEnabled && (
                <>
                  <p>
                    <strong className='text-slate-900'>Médiateur de la consommation :</strong> conformément aux articles
                    L.616-1 et R.616-1 du Code de la consommation, MADSAAS SAS adhère au service de médiation suivant :
                  </p>
                  <div className='p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-1'>
                    <p>
                      <strong className='text-slate-900'>SAS Médiation Solution</strong>
                    </p>
                    <p>222 chemin de la bergerie, 01800 Saint-Jean-de-Niost</p>
                    <p>
                      Saisine en ligne :{' '}
                      <a
                        href='https://www.sasmediationsolution-conso.fr'
                        className='text-blue-600 hover:underline'
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        sasmediationsolution-conso.fr
                      </a>
                    </p>
                    <p>
                      Email :{' '}
                      <a href='mailto:contact@sasmediationsolution-conso.fr' className='text-blue-600 hover:underline'>
                        contact@sasmediationsolution-conso.fr
                      </a>
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
